/**
 * API Client to replace Supabase
 * This client makes HTTP requests to our Cloudflare Workers API
 */

import { authService } from "./authService";

const API_URL = import.meta.env.VITE_API_URL || "/api";

interface ApiError {
  error: string;
  message?: string;
}

class ApiClient {
  constructor() {}

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    const token = authService.getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  private getBaseUrl(): string {
    if (API_URL.startsWith("http://") || API_URL.startsWith("https://")) {
      return API_URL.replace(/\/$/, "");
    }

    if (typeof window !== "undefined" && window.location?.origin) {
      const resolved = new URL(API_URL, window.location.origin).toString();
      return resolved.replace(/\/$/, "");
    }

    return `http://localhost${API_URL}`.replace(/\/$/, "");
  }

  async getSession(): Promise<{ session: { access_token: string } | null; user: any | null }> {
    const token = authService.getAccessToken();
    const cachedUser = authService.getUser();

    if (!token) {
      return { session: null, user: null };
    }

    if (cachedUser) {
      return { session: { access_token: token }, user: cachedUser };
    }

    try {
      const result = await authService.getCurrentUser();
      if (result?.success && result.data?.user) {
        return { session: { access_token: token }, user: result.data.user };
      }
    } catch (error) {
      console.error("Failed to refresh session user:", error);
    }

    return { session: { access_token: token }, user: null };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retry = true,
  ): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    const headers = this.getHeaders();

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      // Handle 401 Unauthorized - Attempt Refresh
      if (response.status === 401 && retry) {
        console.log("Got 401, attempting token refresh...");
        const newToken = await authService.refreshTokens();
        if (newToken) {
          console.log("Token refreshed, retrying request...");
          // Retry the request once with the new token
          return this.request<T>(endpoint, options, false);
        } else {
          console.log("Token refresh failed, redirecting to login");
          // Optionally trigger a logout event or redirect
          // authService.logout() is called inside refreshTokens() if it fails
          throw new Error("Unauthorized");
        }
      }

      // Check content-type before attempting to parse JSON
      const contentType = response.headers.get("content-type");

      if (!contentType || !contentType.includes("application/json")) {
        // Response is not JSON - likely an HTML error page or misconfiguration
        const text = await response.text();

        // Log the first 200 chars for debugging
        console.error(
          `API returned non-JSON response for ${endpoint}:`,
          `Content-Type: ${contentType || "not set"}`,
          `Response preview: ${text.substring(0, 200)}...`,
        );

        throw new Error(
          `API configuration error: Expected JSON response but got ${
            contentType || "unknown content type"
          }. ` +
            `This usually means the API endpoints are not properly deployed.`,
        );
      }

      // Parse JSON response
      let data: any;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error("Failed to parse JSON response:", parseError);
        throw new Error(
          `Invalid JSON response from ${endpoint}: ${
            (parseError as Error).message
          }`,
        );
      }

      // Check if request was successful
      if (!response.ok) {
        throw new Error(
          (data as ApiError).error ||
            `Request failed with status ${response.status}`,
        );
      }

      return data as T;
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  // Profile methods
  async getProfile(username?: string) {
    if (username) {
      return this.request<any>(`/profile/user/${username}`);
    }
    return this.request<any>("/profile");
  }

  async findUserByEmail(email: string) {
    return this.request<any>("/profile/search", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async updateProfile(updates: any) {
    return this.request<any>("/profile", {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async upsertProfile(profile: any) {
    return this.request<any>("/profile", {
      method: "PUT",
      body: JSON.stringify(profile),
    });
  }

  async getUserSettings() {
    return this.request<any>("/profile/settings");
  }

  async updateUserSettings(settings: any) {
    return this.request<any>("/profile/settings", {
      method: "PATCH",
      body: JSON.stringify(settings),
    });
  }

  async upsertUserSettings(settings: any) {
    return this.request<any>("/profile/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }

  async getUserStats(userId?: string) {
    if (userId) {
      return this.request<any>(`/profile/stats/${userId}`);
    }
    return this.request<any>("/profile/stats");
  }

  async upsertUserStats(stats: any) {
    return this.request<any>("/profile/stats", {
      method: "PUT",
      body: JSON.stringify(stats),
    });
  }

  async getPlayHistory() {
    return this.request<any[]>("/profile/history");
  }

  async getLessonReports() {
    return this.request<any[]>("/profile/reports");
  }

  async saveLessonReport(report: any) {
    return this.request<any>("/profile/reports", {
      method: "POST",
      body: JSON.stringify(report),
    });
  }

  async deleteLessonReport(id: string) {
    return this.request<{ success: boolean; id: string }>(
      `/profile/reports/${id}`,
      {
        method: "DELETE",
      },
    );
  }

  // Words methods
  async generateSentences(data: {
    word: string;
    language: string;
    limit?: number;
    targetLangs?: string;
    nativeLanguage?: string;
  }) {
    return this.request<{ results: any[] }>("/ai/generate-sentences", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async generateLexemes(word: string, language: string) {
    return this.request<{ lexemes: string[] }>("/ai/generate-lexemes", {
      method: "POST",
      body: JSON.stringify({ word, language }),
    });
  }

  async getWords(language?: string) {
    const query = language ? `?language=${encodeURIComponent(language)}` : "";
    return this.request<any[]>(`/words${query}`);
  }

  async getWord(id: string) {
    return this.request<any>(`/words/${id}`);
  }

  async createWord(word: string, language: string) {
    return this.request<any>("/words", {
      method: "POST",
      body: JSON.stringify({ word, language }),
    });
  }

  async deleteWord(id: string) {
    return this.request<{ message: string }>(`/words/${id}`, {
      method: "DELETE",
    });
  }

  // Sentences methods
  async getSentences(language?: string) {
    const query = language ? `?language=${encodeURIComponent(language)}` : "";
    return this.request<any[]>(`/sentences${query}`);
  }

  async getSentence(id: number) {
    return this.request<any>(`/sentences/${id}`);
  }

  async createSentence(data: {
    sentence: string;
    translation: string;
    language: string;
    translationLanguage: string;
    wordIds?: string[];
  }) {
    return this.request<any>("/sentences", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async createSentences(
    data: Array<{
      sentence: string;
      translation: string;
      language: string;
      translationLanguage: string;
      wordIds?: string[];
    }>,
  ) {
    return this.request<any[]>(`/sentences`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateSentence(id: number, updates: any) {
    return this.request<any>(`/sentences/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async deleteSentence(id: number | string) {
    return this.request<{ message: string }>(`/sentences/${id}`, {
      method: "DELETE",
    });
  }

  async toggleFavorite(sentenceId: number) {
    return this.request<{ favorited: boolean }>(
      `/sentences/${sentenceId}/favorite`,
      {
        method: "POST",
      },
    );
  }

  async getFavorites() {
    return this.request<any[]>("/sentences/favorites");
  }

  // SRS methods
  async getDueSentences(limit: number = 20) {
    return this.request<any[]>(`/srs/due?limit=${limit}`);
  }

  async recordReview(
    sentenceId: number,
    difficulty: number,
    responseTime?: number,
  ) {
    return this.request<{
      success: boolean;
      nextReview: string;
      interval: number;
    }>("/srs/review", {
      method: "POST",
      body: JSON.stringify({ sentenceId, difficulty, responseTime }),
    });
  }

  async getReviewStats() {
    return this.request<{
      total: number;
      due: number;
      reviewsToday: number;
      totalReviews: number;
    }>("/srs/stats");
  }

  // Smart Read methods
  async getArticles(language?: string) {
    const query = language ? `?language=${encodeURIComponent(language)}` : "";
    return this.request<any[]>(`/smart-read/articles${query}`);
  }

  async getArticle(id: string) {
    return this.request<any>(`/smart-read/articles/${id}`);
  }

  async createArticle(data: {
    title: string;
    content: string;
    language: string;
    difficulty?: string;
    metadata?: any;
    translations?: any[];
    wordTranslations?: any[];
  }) {
    return this.request<any>("/smart-read/articles", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async searchArticles(params: { query?: string; language?: string }) {
    const queryParams = new URLSearchParams();
    if (params.query) queryParams.append("query", params.query);
    if (params.language) queryParams.append("language", params.language);
    return this.request<any[]>(
      `/smart-read/articles?${queryParams.toString()}`,
    );
  }

  async getWordTranslation(word: string) {
    return this.request<any>(
      `/smart-read/word-translations/${encodeURIComponent(word)}`,
    );
  }

  // TTS method
  async tts(sentence: string, language: string) {
    return this.request<{ success: boolean; audio: string; format: string }>(
      "/tts",
      {
        method: "POST",
        body: JSON.stringify({ sentence, language }),
      },
    );
  }

  async generateArticle(data: {
    topic: string;
    length: "short" | "long";
    language: string;
    nativeLanguage?: string;
  }) {
    // 1. Generate content via AI
    const aiResponse = await this.request<any>("/ai/generate-article", {
      method: "POST",
      body: JSON.stringify(data),
    });

    // 2. Save to database via smart-read/articles
    const articlePayload = {
      title: aiResponse.article.topic,
      content: aiResponse.article.content,
      language: aiResponse.article.language,
      difficulty: "intermediate",
      metadata: { length: aiResponse.article.length },
      translations: Object.entries(aiResponse.translations).map(
        ([lang, content]) => ({
          language: lang,
          content,
        }),
      ),
      wordTranslations: aiResponse.word_translations,
    };

    return this.createArticle(articlePayload);
  }

  async saveWordToCollection(
    word: string,
    language: string,
    nativeLanguage: string = "english",
  ) {
    try {
      // Create or get existing word
      const wordData = await this.createWord(word, language);
      const wordId = wordData.id || wordData.word?.id; // Handle different response structures

      if (!wordId) {
        console.error("Failed to get word ID after creation");
        return wordData;
      }

      // Generate sentences in background
      this.generateSentences({
        word,
        language,
        limit: 3,
        nativeLanguage,
      })
        .then(async (response) => {
          if (response.results && response.results.length > 0) {
            console.log(
              `Generated ${response.results.length} sentences for ${word}`,
            );

            // Save sentences to database
            const sentencesToCreate = response.results.map((s) => {
              // Handle translation extraction
              let translation = "";
              if (s.translations && s.translations.length > 0) {
                translation = s.translations[0].text;
              }

              return {
                sentence: s.text,
                translation: translation,
                language: language,
                translationLanguage: nativeLanguage,
                wordIds: [wordId],
              };
            });

            if (sentencesToCreate.length > 0) {
              await this.createSentences(sentencesToCreate);
              console.log(
                `Saved ${sentencesToCreate.length} sentences for ${word}`,
              );
            }
          }
        })
        .catch((err) =>
          console.error(
            "Background sentence generation and saving failed:",
            err,
          ),
        );

      return wordData;
    } catch (error) {
      console.error("Save word to collection error:", error);
      throw error;
    }
  }

  async markWordAsKnown(wordId: string) {
    return this.request<any>(`/words/${wordId}/master`, {
      method: "POST",
      headers: {
        // Explicitly set headers if needed, but getHeaders() should handle it
      },
    });
  }

  async deleteArticle(id: string) {
    return this.request<{ message: string }>(`/smart-read/articles/${id}`, {
      method: "DELETE",
    });
  }

  // AI methods
  async translate(text: string, sourceLang: string, targetLang: string) {
    return this.request<{
      translation: string;
      sourceLang: string;
      targetLang: string;
    }>("/ai/translate", {
      method: "POST",
      body: JSON.stringify({ text, sourceLang, targetLang }),
    });
  }

  async generateSentence(word: string, language: string, difficulty?: string) {
    return this.request<any>("/ai/generate-sentence", {
      method: "POST",
      body: JSON.stringify({ word, language, difficulty }),
    });
  }

  async getSmartPractice(
    userLevel: string,
    targetLanguage: string,
    focusAreas?: string[],
  ) {
    return this.request<any>("/ai/smart-practice", {
      method: "POST",
      body: JSON.stringify({ userLevel, targetLanguage, focusAreas }),
    });
  }

  async generateSmartPracticeReport(data: {
    messages: any[];
    words: any[];
    targetLanguage: string;
    nativeLanguage: string;
    timestamp: string;
    words_used: any[];
  }) {
    return this.request<any>("/ai/smart-practice/report", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async chatWithAI(data: {
    messages: any[];
    words: any[];
    targetLanguage: string;
    nativeLanguage: string;
  }) {
    return this.request<{ message: string }>("/ai/chat", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Onboarding methods
  async getOnboardingProgress() {
    return this.request<any>("/onboarding/progress");
  }

  async updateOnboardingProgress(data: any) {
    return this.request<any>("/onboarding/progress", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getSkillAssessments() {
    return this.request<any[]>("/onboarding/assessments");
  }

  async saveSkillAssessment(data: any) {
    return this.request<any>("/onboarding/assessments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async generateAssessmentQuestions(data: {
    language: string;
    nativeLanguage?: string;
    difficultyLevel?: number;
    count?: number;
    questionTypes?: string[];
  }) {
    return this.request<{ questions: any[] }>(
      "/ai/generate-assessment-questions",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }

  async generateAdaptiveQuestion(data: {
    language: string;
    nativeLanguage?: string;
    previousAnswers?: any[];
    nextDifficultyLevel?: number;
  }) {
    return this.request<{ question: any | null }>(
      "/ai/generate-adaptive-question",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
